import React from "react";
import { Badge, Box, Text, VStack } from "@chakra-ui/react";

const cardProps = {
  bg: "white",
  border: "1px solid",
  borderColor: "#d5e4ea",
  borderRadius: "lg",
  p: 5,
};

type Scenario = {
  title: string;
  when: string;
  action: string;
  header: string;
};

const scenarios: Scenario[] = [
  {
    title: "6.6.1 and 6.6.2 are not certified",
    when: "The version identifier and the parent version identifier are already known. The certificate code is not.",
    action: "Open the version and add the certificate code when it arrives. Parent version of 6.6.2 is 6.6.1. Type lock when the details must stay unchanged.",
    header: "6.6.2 sends current version 6.6.2 and parent version 6.6.1. 6.6.1 sends parent version 6.6.1. Neither version is certified until you mark it certified.",
  },
  {
    title: "6.6.2 is ready, and Customer A installs it before certification",
    when: "The new installer is finished. Revenu Québec has not certified it. Make this change at night, while the store is not selling.",
    action: "On the Version tab, create 6.6.2. It copies the certified 6.6.1 profile, sets the parent to 6.6.1, and stays pending. Install 6.6.2 only on Customer A.",
    header: "Customer A, on the next day's first bill: current version 6.6.2, parent version 6.6.1, version identifier and certificate code still from 6.6.1. Customers B and C stay on 6.6.1.",
  },
  {
    title: "Customer B installs 6.6.2 later, still not certified",
    when: "6.6.2 is still pending.",
    action: "No new profile. Install the same 6.6.2 build on Customer B.",
    header: "Customer B joins the same pending header as Customer A. Customer C stays on 6.6.1.",
  },
  {
    title: "The version identifier arrives",
    when: "Revenu Québec sends the 16-character version identifier before the certificate. Make this change at night.",
    action: "Open pending 6.6.2, paste the identifier, and save. The certificate code stays the parent's.",
    header: "Customers A and B send the new identifier on the next day's first bill. The example shape is 0000000000004A10 (Example). Replace it with the identifier Revenu Québec sends.",
  },
  {
    title: "6.6.2 is certified",
    when: "Revenu Québec sends the certificate code. Make this change at night.",
    action: "Open Certify. The parent version starts as this version. Change the certificate code and any other field. Type certify, then choose Certify.",
    header: "Customers A and B, on the next day's first bill: current version 6.6.2, parent version 6.6.2, the certified identifier, and the new certificate code. The example certificate code is EXAMPLE000000 (Example). Customer C stays on 6.6.1 until that store installs 6.6.2.",
  },
  {
    title: "Customer C installs after certification",
    when: "6.6.2 is already certified.",
    action: "Install 6.6.2. No new profile.",
    header: "Customer C's first bill sends the certified 6.6.2 header. Current version and parent version are both 6.6.2.",
  },
  {
    title: "6.6.3 starts after 6.6.2 is certified",
    when: "The next installer is ready and 6.6.2 is certified.",
    action: "Create pending 6.6.3. Its parent is 6.6.2.",
    header: "A workstation that installs 6.6.3 sends current version 6.6.3 and parent version 6.6.2 until 6.6.3 is certified.",
  },
  {
    title: "A newer build starts while 6.6.2 is still pending",
    when: "6.6.2 exists and is not certified yet.",
    action: "Create the newer version and set the parent to 6.6.1.",
    header: "Current version is the new build. Parent version is 6.6.1.",
  },
  {
    title: "The installed version has no profile",
    when: "A workstation reports an installed version that is not on the Version tab.",
    action: "Create the profile for that installed version before that workstation sells.",
    header: "The Version tab lists the workstation under Version mismatches.",
  },
];

const VersionManagementGuide: React.FC = () => (
  <VStack align="stretch" spacing={3}>
    <Box {...cardProps}>
      <Text fontWeight="600" mb={2}>How a change reaches the bill</Text>
      <Text fontSize="sm" color="gray.600">
        Customers A, B, and C below are examples. Example identifiers and certificate codes stay on this tab. They are not saved as live profiles.
      </Text>
      <Text fontSize="sm" color="gray.600" mt={2}>
        Edit a version at night, while that store is not selling. The next day's first bill reads the profile and sends the new current version, parent version, version identifier, and certificate code.
      </Text>
    </Box>
    {scenarios.map((scenario, index) => (
      <Box key={scenario.title} {...cardProps}>
        <Badge mb={2}>{index + 1}</Badge>
        <Text fontWeight="600" mb={3}>{scenario.title}</Text>
        <Text fontSize="sm" color="gray.700" mb={2}><Text as="span" fontWeight="600">When. </Text>{scenario.when}</Text>
        <Text fontSize="sm" color="gray.700" mb={2}><Text as="span" fontWeight="600">What you do. </Text>{scenario.action}</Text>
        <Text fontSize="sm" color="gray.700"><Text as="span" fontWeight="600">Header. </Text>{scenario.header}</Text>
      </Box>
    ))}
  </VStack>
);

export default VersionManagementGuide;
